import { ProvisionType } from '@wix/ambassador-funnel-intro-v1-meta-site-provision-descriptor/types'
import { Box, LinearProgressBar, Text } from '@wix/design-system'
import { useContent, useMetadata, useNext } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { provisionMetasite } from '../../../../api/FunnelIntroSiteProvisioningService/provisionMetasite'
import { usePageLogger } from '../../../../bi/mfi'
import { resolveCategoryFromBusinessTerm } from '../../../../category'
import { useFlowContext } from '../../../../hooks/useFlowContext'
import { WixMobileBackButton } from '../../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileHeader } from '../../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../../reusable/WixMobilePage/WixMobilePage'
import { STORES_FUNNEL_QUERY_PARAMS } from '../StoresFunnelUpgrade/StoresFunnelUpgrade'
import { Content } from './StoresFunnelLoader.data'
import { storesFunnelLoaderDataHooks as DataHooks } from './StoresFunnelLoader.dataHooks'
import s from './StoresFunnelLoader.scss'

const MOCKED_TIMEOUT_MS = 2000

export const StoresFunnelLoader: FC = () => {
  const [flowContext] = useFlowContext()
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0)
  const { content } = useContent<Content>()
  const next = useNext()
  const pageLogger = usePageLogger()
  const [{ biSlug: flowBiSlug }] = useMetadata()
  const [searchParams] = useSearchParams()

  const { title } = content.question.content
  const phases = content.question.options
  const storesCategory = searchParams.get(STORES_FUNNEL_QUERY_PARAMS.categoryKey) || ''
  const templateId = searchParams.get(STORES_FUNNEL_QUERY_PARAMS.templateId) || null
  const businessName = flowContext?.answers[content.business_name_key]?.values?.[0] || 'My Store'

  const onBackButtonClick = () => {
    pageLogger.back({
      payload: {},
    })

    window.history.back()
  }

  const provisionMetaSite = async () => {
    const storesBusinessTerm = {
      id: '6569ce47-806c-4a25-8000-03b0d8a79360',
      name: 'Online Store',
      segmentationIndustryId: 'not_classified',
      businessType: 'type_store',
      postRegVertical: 'online-store',
      mainSegmentationIndustryId: 'not_classified',
      aliases: [
        'Ecom Store',
        'Ecom Shop',
        'Ecommerce Shop',
        'Ecommerce Store',
        'Online Retailer',
        'Online Merchant',
        'Online Shop',
        'Store',
        'Shop',
        'Retail Shop',
        'Retail Store',
        'Retailer',
        'Sales',
      ],
      businessSegmentId: 'consumer-stores',
    }
    const { industryId, structureId, displayName, postRegVertical, businessTermId, businessSegmentId } =
      resolveCategoryFromBusinessTerm(storesBusinessTerm, storesBusinessTerm.businessSegmentId)

    const response = await provisionMetasite({
      type: ProvisionType.HTML_DRAFT,
      origin: flowBiSlug,
      metadata: {
        flow: flowBiSlug,
      },
      templateId,
      additionalIntroData: {
        businessName,
        industryId,
        structureId,
        category: {
          industryId,
          structureId,
          displayName,
          postRegVertical,
          businessTermId,
          businessSegmentId,
        },
        selectedApps: [],
      },
    })

    const metaSiteId = response.data.metaSiteProvisionDescriptor?.metaSiteId || undefined

    // save({ provisionedMetaSiteId: metaSiteId })
    // const dashboardUrl = `https://manage.wix.com/dashboard/${metaSiteId}/setup`

    return metaSiteId
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    let phaseInterval: NodeJS.Timeout | null = null
    let isCompleted = false
    let provisionPromise: Promise<string | null> | null = null

    // Start provisionMetaSite immediately
    provisionPromise = provisionMetaSite()
      .catch(error => {
        console.error('Failed to provision meta site:', error)
        return null
      })
      .then(result => result || null)

    // Start the phase progression
    const startPhaseProgression = () => {
      phaseInterval = setInterval(() => {
        setCurrentPhaseIndex(prevIndex => {
          const nextIndex = prevIndex + 1

          // If we've reached the last phase, wait for provisionMetaSite to complete
          if (nextIndex + 1 >= phases.length) {
            clearInterval(phaseInterval!)
            phaseInterval = null

            // Wait for provisionMetaSite to complete
            provisionPromise!.then(metaSiteId => {
              handleCompletion(metaSiteId)
            })

            return prevIndex // Stay at the last phase
          }

          return nextIndex
        })
      }, MOCKED_TIMEOUT_MS)
    }

    // Handle completion
    const handleCompletion = (metaSiteId: string | null) => {
      if (isCompleted) {
        return
      }

      isCompleted = true
      if (phaseInterval) {
        clearInterval(phaseInterval)
      }

      // Ensure loader reaches 100%
      setCurrentPhaseIndex(phases.length)

      // Call next function with success value
      pageLogger.answer({
        answer: { status: 'completed', metaSiteId: metaSiteId || 'no_msid' },
      })

      next({ [content.question.questionKey]: { values: [metaSiteId || 'no_msid'] } })
    }

    // Start phase progression
    startPhaseProgression()

    return () => {
      if (phaseInterval) {
        clearInterval(phaseInterval)
      }
    }
  }, [phases.length, content.question.questionKey, next, pageLogger, flowBiSlug, templateId, businessName])

  const progressValue = phases.length > 0 ? (100 / phases.length) * (currentPhaseIndex + 1) : 0
  const currentPhase = phases[currentPhaseIndex]

  return (
    <WixMobilePage>
      <WixMobileHeader
        leftContent={
          (content.back && (
            <WixMobileBackButton dataHook={DataHooks.back()} onBack={onBackButtonClick} label={content.back} />
          )) ||
          undefined
        }
      />
      <Box direction="vertical" align="center" marginTop="30vh">
        <Box marginBottom="24px">
          <WixMobileHeading textAlign="center" dataHook={DataHooks.title()}>
            {title}
          </WixMobileHeading>
        </Box>

        <Box padding="0px 24px" width="100%" boxSizing="border-box" marginBottom="16px">
          <LinearProgressBar showProgressIndication value={progressValue} data-hook={DataHooks.progressBar()} />
        </Box>

        <Box>
          {currentPhase && (
            <Text className={s.phaseText} data-hook={DataHooks.phaseText({ index: currentPhaseIndex })}>
              {currentPhase.title}
            </Text>
          )}
        </Box>
      </Box>
    </WixMobilePage>
  )
}
